import type { MovementTuning } from '@battle/content';
import {
  type CreatureState,
  createFixedStep,
  createWorld,
  decodeArenaCollision,
  type Input,
  initPhysics,
  NEUTRAL_INPUT,
  type SimEvent,
  type SimWorld,
} from '@battle/sim';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as THREE from 'three/webgpu';
import { type CameraTuning, createFollowCamera } from '../camera/follow-camera.ts';
import { loadCreature } from '../creatures/creature-model.ts';
import { createCreatureView, type ViewTuning } from '../creatures/creature-view.ts';
import { createInput, type InputSettings } from '../input/input.ts';

/**
 * M1 playable: the local player drives one creature through the deterministic sim at 60 Hz;
 * the other creature stands on SPAWN_B as a training dummy. Rendering interpolates between
 * the last two sim states.
 */
export interface Game {
  /** Online games hide movement tuning: local changes would mispredict against the server. */
  readonly online: boolean;
  update(dt: number, elapsed: number): void;
  readonly status: () => GameStatus;
  /** Live-tunable objects for the Training panel. */
  readonly tuning: {
    movement: () => MovementTuning;
    setMovement: (t: MovementTuning) => void;
    camera: CameraTuning;
    input: InputSettings;
    view: ViewTuning;
    resetPlayer: () => void;
  };
}

export interface GameStatus {
  /** Online: room code and a copyable invite link. */
  room?: string;
  net?: string;
  creature: string;
  mode: string;
  speed: number;
  stamina: number;
  staminaMax: number;
  locked: boolean;
  device: string;
  lockOn: boolean;
}

const ARENA = '/content/arenas/sunken-grove';

/**
 * Loads the arena collision into a fresh sim world and adds the greybox gameplay pieces the
 * arena art doesn't cover yet (pillars, dais, ramp) as rough stone. `?col` also shows the floor
 * and boundary collision as wireframe. Shared by offline Training and online play.
 */
export async function setupArena(scene: THREE.Scene, params: URLSearchParams) {
  const [, colBytes, greybox] = await Promise.all([
    initPhysics(),
    fetch(`${ARENA}/arena.col`).then(async (r) => {
      if (!r.ok) throw new Error(`arena.col: HTTP ${r.status}`);
      return new Uint8Array(await r.arrayBuffer());
    }),
    new GLTFLoader().loadAsync(`${ARENA}/greybox.glb`),
  ]);
  const arena = decodeArenaCollision(colBytes);
  const world = createWorld(arena);
  const stone = new THREE.MeshStandardNodeMaterial({ color: '#8a8175', roughness: 0.9 });
  const wire = new THREE.MeshBasicNodeMaterial({
    color: '#39f',
    wireframe: true,
    transparent: true,
    opacity: 0.35,
  });
  greybox.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const isShell = /COL_(floor|boundary)/.test(mesh.name);
    mesh.visible = !isShell || params.has('col');
    mesh.material = isShell ? wire : stone;
    mesh.castShadow = mesh.receiveShadow = !isShell;
  });
  scene.add(greybox.scene);
  return { arena, world };
}

/** Ground height under (x, z) by casting down from y, for foot and slope layers. */
export function groundProbe(world: SimWorld) {
  return (x: number, y: number, z: number) => {
    const toi = world.castRay({ x, y, z }, { x: 0, y: -1, z: 0 }, 4);
    return toi === null ? null : y - toi;
  };
}

export async function createGame(opts: {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGPURenderer;
  canvas: HTMLElement;
  params: URLSearchParams;
}): Promise<Game> {
  const { scene, camera, renderer, canvas, params } = opts;
  const playerId = params.get('creature') === 'rillwing' ? 'rillwing' : 'kilnback';
  const dummyId = playerId === 'kilnback' ? 'rillwing' : 'kilnback';

  const [{ arena, world }, playerModel, dummyModel] = await Promise.all([
    setupArena(scene, params),
    loadCreature(renderer, playerId),
    loadCreature(renderer, dummyId),
  ]);

  const spawnA = arena.spawns.find((s) => s.name === 'SPAWN_A') ?? { x: 0, y: 0, z: -9, yaw: 0 };
  const spawnB = arena.spawns.find((s) => s.name === 'SPAWN_B') ?? {
    x: 0,
    y: 0,
    z: 9,
    yaw: Math.PI,
  };
  const player = world.addCreature(playerId, { ...spawnA, y: spawnA.y + 0.1 });
  const dummy = world.addCreature(dummyId, { ...spawnB, y: spawnB.y + 0.1 });

  scene.add(playerModel.root, dummyModel.root);
  const views = new Map([
    [
      player.handle,
      createCreatureView(playerModel, playerId, world.tuningOf(player.handle).dodge.ticks),
    ],
    [
      dummy.handle,
      createCreatureView(dummyModel, dummyId, world.tuningOf(dummy.handle).dodge.ticks),
    ],
  ]);

  const input = createInput(canvas);
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cam = createFollowCamera(camera, world.castRay, reduceMotion);
  cam.yaw = spawnA.yaw;
  cam.scale = playerId === 'kilnback' ? 1 : 1.15;

  const fixed = createFixedStep();
  const snapshot = () => world.creatures.map((c) => structuredClone(c) as CreatureState);
  let prev = snapshot();
  let curr = snapshot();
  let lockOn = false;

  const route = (events: SimEvent[]) => {
    for (const e of events) {
      views.get(e.handle)?.onEvent(e);
      if (e.handle !== player.handle) continue;
      if (e.type === 'land' && e.impact > 9) cam.addTrauma(Math.min(0.5, (e.impact - 9) / 12));
      if (e.type === 'dodge') cam.addTrauma(0.08);
    }
  };

  const debug = { forcedYaw: null as number | null };
  window.__battleDebug = {
    world,
    camera,
    scene,
    setCameraYaw(yaw: number) {
      debug.forcedYaw = yaw;
    },
  };

  const playerView = views.get(player.handle);
  return {
    online: false,
    tuning: {
      movement: () => world.tuningOf(player.handle),
      setMovement: (t) => world.setTuning(player.handle, t),
      camera: cam.tuning,
      input: input.settings,
      view: (playerView as NonNullable<typeof playerView>).tuning,
      resetPlayer: () => {
        world.teleport(player.handle, { ...spawnA, y: spawnA.y + 0.1 }, spawnA.yaw);
        world.teleport(dummy.handle, { ...spawnB, y: spawnB.y + 0.1 }, spawnB.yaw);
        cam.yaw = spawnA.yaw;
        prev = curr = snapshot();
      },
    },
    status: () => ({
      creature: playerId,
      mode: player.sprinting ? 'sprint' : player.mode,
      speed: Math.hypot(player.vel.x, player.vel.z),
      stamina: player.stamina,
      staminaMax: world.tuningOf(player.handle).stamina.max,
      locked: input.locked,
      device: input.device,
      lockOn,
    }),
    update(dt, elapsed) {
      if (input.consumePress('lockOn')) {
        lockOn = !lockOn;
        cam.lockTarget = lockOn ? () => dummy.pos : null;
      }
      const look = input.takeLook(dt);
      if (debug.forcedYaw !== null) {
        cam.yaw = debug.forcedYaw;
        debug.forcedYaw = null;
      }

      const steps = fixed.advance(dt);
      for (let i = 0; i < steps; i++) {
        const mine: Input = input.sample(cam.yaw, cam.pitch);
        prev = curr;
        route(
          world.step(
            new Map([
              [player.handle, mine],
              [dummy.handle, NEUTRAL_INPUT],
            ]),
          ),
        );
        curr = snapshot();
      }
      input.endFrame();

      const ground = groundProbe(world);
      const toward = (from: CreatureState, to: CreatureState) => ({
        yaw: Math.atan2(to.pos.x - from.pos.x, to.pos.z - from.pos.z),
        pitch: 0,
      });
      for (let i = 0; i < curr.length; i++) {
        const c = curr[i] as CreatureState;
        const isPlayer = c.handle === player.handle;
        const target = isPlayer ? dummy : player;
        const look =
          isPlayer && !lockOn ? { yaw: cam.yaw, pitch: cam.pitch * 0.6 } : toward(c, target);
        views.get(c.handle)?.sync(prev[i] ?? c, c, fixed.alpha, dt, elapsed, { look, ground });
      }
      const p = playerModel.root.position;
      cam.update(dt, { x: p.x, y: p.y, z: p.z }, { sprinting: player.sprinting, look });
    },
  };
}
